import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomSwal, maskEmail, maskMobile } from "../services/customSwal";
import { forgetrequest, PasswordRest } from "../api/loginApiService";
import "../styles/Login.css";

const ForgotPassword: React.FC = () => {
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");
  const [receiver, setReceiver] = useState("");
  const navigate = useNavigate();

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow only letters, numbers, and underscore
    if (/^[a-zA-Z0-9_]*$/.test(value)) {
      setUsername(value);
      if (error === "Username is required.") setError("");
    }
  };

  const handleReceiverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    // Email/Mobile: remove invalid characters for numbers, allow @ . _ - for emails
    if (/^\d*$/.test(val)) {
      // Only digits allowed for mobile
      val = val.slice(0, 10); // max 10 digits for mobile
    } else {
      // For email, allow letters, numbers, @, ., _, -
      val = val.replace(/[^\w@.-]/g, '');
    }

    setReceiver(val);

    if (
      error === "Email or Mobile is required." ||
      error === "Invalid email format." ||
      error === "Mobile number must be exactly 10 digits."
    ) {
      setError("");
    }
  };
  const showResetPopup = async (contactLabel: string) => {
    return CustomSwal.fire({
      title: "Reset Password",
      html: `
        <label for="swal-otp" style="display:block;text-align:center;margin-bottom:4px;">${contactLabel}<br>OTP valid within 5 minutes</label>
        <input id="swal-otp" class="swal2-input" placeholder="Enter OTP" maxlength="4">

        <label for="swal-pass" style="display:block;text-align:left;margin-top:10px;margin-bottom:4px;">New Password</label>
        <input id="swal-pass" type="password" class="swal2-input" placeholder="Enter new password">

        <label for="swal-confirm" style="display:block;text-align:left;margin-top:10px;margin-bottom:4px;">Confirm Password</label>
        <input id="swal-confirm" type="password" class="swal2-input" placeholder="Confirm new password">
      `,
      focusConfirm: false,
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: "Reset",
      preConfirm: () => {
        const otp = (document.getElementById("swal-otp") as HTMLInputElement).value.trim();
        const password = (document.getElementById("swal-pass") as HTMLInputElement).value.trim();
        const confirm = (document.getElementById("swal-confirm") as HTMLInputElement).value.trim();

        if (!otp || !password || !confirm) {
          CustomSwal.showValidationMessage("All fields are required");
          return false;
        }
        if (otp.length !== 4) {
          CustomSwal.showValidationMessage("OTP must be exactly 4 digits");
          return false;
        }
        if (password.length < 6 || password.length > 10) {
          CustomSwal.showValidationMessage("Password must be 6–10 characters");
          return false;
        }
        if (password !== confirm) {
          CustomSwal.showValidationMessage("Passwords do not match");
          return false;
        }

        return { otp, password };
      },
    });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const currentUsername = username.trim();
    const currentReceiver = receiver.trim();

    const isMobile = /^\d+$/.test(currentReceiver);
    const isEmail = /^[^\s@]+@[^\s@]+\.(com|in|edu)$/i.test(currentReceiver);

    // Validation
    if (!currentUsername) return setError("Username is required.");
    if (!currentReceiver) return setError("Email or Mobile is required.");
    if (isMobile && currentReceiver.length !== 10) return setError("Mobile number must be exactly 10 digits.");
    if (!isMobile && !isEmail) return setError("Invalid email format.");

    try {
      const payload = isMobile ? { mobile: currentReceiver } : { email: currentReceiver };
      const apiResponse = await forgetrequest(currentUsername, payload);
      const { result, message } = apiResponse;

      if (result === "-1") return CustomSwal.fire("Error", message, "error");
      if (result === "0") return CustomSwal.fire("Warning", message, "warning");

      const contactLabel = isMobile
        ? `OTP sent to Mobile: ${maskMobile(currentReceiver)}`
        : `OTP sent to Email: ${maskEmail(currentReceiver)}`;

      let retry = true;
      while (retry) {
        retry = false; // reset retry flag

        const { value: formValues } = await showResetPopup(contactLabel);

        if (!formValues) break; // user cancelled

        const apiResponse = await PasswordRest(currentUsername, payload, formValues.otp, formValues.password);
        const { result, message } = apiResponse;

        if (result === "1") {
          await CustomSwal.fire("Password Reset", "Your password has been successfully reset.", "success");
          navigate("/");
        } else if (result === "3" || result === "2") {
          await CustomSwal.fire("Error", message || "Invalid OTP or missing data. Please try again.", "error");
          retry = true; // reopen popup
        } else {
          await CustomSwal.fire("Error", message || "Something went wrong.", "error");
        }
      }
    } catch (err) {
      console.error(err);
      CustomSwal.fire("Error", "Something went wrong. Please try again.", "error");
    }
  };

  return (
    <div className="full-background">
      <video autoPlay muted loop playsInline className="bg-video">
        <source src="/assets/BgVideo.mp4" type="video/mp4" />
      </video>
      {/* <div className="overlay-gradient"></div> */}
      <div className="left-text"><h1>PSG-ERP</h1></div>

      <div className="login-card">
        <div className="login-header-bar"><h4 className="login-title">Reset Password</h4></div>
        <form onSubmit={onSubmit} className="loginForm" autoComplete="off">
          {error && <span className="text-danger">{error}</span>}

          <div className={`input-group ${error === "Username is required." ? "invalid" : ""}`}>
            <i className="fas fa-user"></i>
            <input type="text" placeholder="Registered Login ID" value={username} onChange={handleUsernameChange} />
          </div>

          <div className={`input-group ${["Email or Mobile is required.", "Invalid email format.", "Mobile number must be exactly 10 digits."].includes(error)
            ? "invalid" : ""
            }`}>
            <i className="fas fa-envelope"></i>
            <input
              type="text"
              placeholder="Registered email or mobile"
              value={receiver}
              onChange={handleReceiverChange}
              maxLength={/^\d+$/.test(receiver) ? 10 : undefined}
            />
          </div>

          <button type="submit" className="login-btn">Send OTP</button>

          <div className="signup-link">
            Remembered your password?{" "}
            <span onClick={() => navigate("/")} style={{ cursor: "pointer", color: "#007bff" }}>
              Login
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
